import type { NwsObservation, NwsForecast, NwsPeriod } from '@/lib/nws/types'
import { weatherIcon } from '@/lib/nws/weather-icon'
import { tempF, windMph, compass, pressureInHg } from '@/lib/nws/units'

/** Derive the display-ready current-conditions values from the raw NWS data. */
export function useConditions(
  observation: NwsObservation | null,
  forecast: NwsForecast | undefined,
  currentPeriod: NwsPeriod | undefined,
) {
  const conditionText =
    observation?.textDescription || currentPeriod?.shortForecast || 'Current conditions'
  const isDay = currentPeriod?.isDaytime ?? true
  const iconKey = weatherIcon(conditionText, isDay)

  const obsTemp = tempF(observation?.temperature)
  const temp = obsTemp ?? currentPeriod?.temperature ?? null
  const feels = tempF(observation?.heatIndex) ?? tempF(observation?.windChill) ?? obsTemp ?? temp
  // Differential vs the OBSERVED temp only (same source as feels) → never mixes obs
  // feels with a forecast temp. Null when there's no observation to compare against.
  const feelsDiff =
    obsTemp != null && feels != null ? Math.round(feels) - Math.round(obsTemp) : null
  const humidity = observation?.relativeHumidity?.value ?? null
  const wind = windMph(observation?.windSpeed)
  const windDir = compass(observation?.windDirection?.value)
  const windBearing = observation?.windDirection?.value ?? null
  const pressure = pressureInHg(observation?.barometricPressure)
  const { hi, lo } = todayHiLo(forecast?.periods ?? [])

  return {
    conditionText,
    isDay,
    iconKey,
    temp,
    feels,
    feelsDiff,
    humidity,
    wind,
    windDir,
    windBearing,
    pressure,
    hi,
    lo,
  }
}

function todayHiLo(periods: NwsPeriod[]): { hi: number | null; lo: number | null } {
  let hi: number | null = null
  let lo: number | null = null
  for (const p of periods.slice(0, 3)) {
    if (p.isDaytime && hi == null) hi = p.temperature
    if (!p.isDaytime && lo == null) lo = p.temperature
  }
  return { hi, lo }
}
